import { Stack } from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'

import { fgDark } from '@/_vars'
import LogoTagline from '@/assets/logo/Logo_Tagline.svg'
import Button from '@/atoms/Button/Button'
import { Checkbox } from '@/atoms/Checkbox'
import { ButtonLoader } from '@/atoms/Loader/Loader'
import TextField from '@/atoms/TextField/TextField'
import { providerLogin, providerLoginSSO, providerPwdReset } from '@/lib/auth'
import { isIE } from '@/lib/browser'
import { isUsingStorageFallback } from '@/lib/browserStorage'
import { captureAndLogError } from '@/lib/error'
import { Trans, withTranslation, WithTranslation } from '@/lib/i18n/i18n'
import { changeLanguage } from '@/lib/i18n/locale-utils'
import { getCurrentLocale } from '@/lib/i18n/locale-utils'
import { openExternalUrl } from '@/lib/openExternalUrl'
import { Routes } from '@/lib/routes'
import { eventStopper, isBoolPredicate, parseQueryString } from '@/lib/utils'
import TermsOfService from '@/molecules/TermsOfService/TermsOfService'
import { withRouter, WithRouterProps } from '@/pages/App/withRouter'
import {
	BackToLogin,
	Copyright,
	FormTitle,
	Header,
	Image,
	LoginBox,
	LoginContainer,
	LoginForm,
	LoginImage,
	LoginImagesBox,
	LoginPage,
	LoginPageContent,
	LogoImage,
	RecoveryText,
	ResponseText,
	StyledCarousel,
} from '@/pages/Login/Login.components'
import { CompanyTypeModalActions, IAction } from '@/redux/actions'
import { getStore } from '@/redux/stores'
import { ApplicationState } from '@/redux/stores/types'
import { brightTurquoise, darkBlue, yellow } from '@/refactor/colors'
import styled from '@/styles'
import { LoginStatus, PageProps } from '@/travelsuit'
import { AsyncTrackResult, track } from '@/travelsuit/analytics'
import { AuthUser } from '@/types/auth'
import { Locales } from '@/types/locale'
import { User } from '@/types/user'

import { generateAutoAppName } from '../ManageSyncSSOConfig/utils'
import LoginBlueImage from './login/login_blue.svg?raw'
import LoginGreenImage from './login/login_green.svg?raw'
import LoginYellowImage from './login/login_yellow.svg?raw'

const InfoBlock = styled.div`
	color: ${fgDark};
	line-height: 1.5;

	& a {
		line-height: 1.7;
	}
`

const IeError = styled.div``

const ChromeLink = styled.div``

const IeSection = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	text-align: center;
	height: 150px;
	line-height: 1.5;

	& > ${ChromeLink} {
		font-weight: 600;
		cursor: pointer;
	}

	& > ${IeError} {
		color: $accent-primary;
		font-weight: 600;
	}
`

interface OwnProps extends PageProps {
	//
}

interface StateProps {
	termsAccepted: boolean | null
	user: AuthUser | null
}

interface DispatchProps {
	showModalWindow(): void
}

type IProps = OwnProps & StateProps & DispatchProps & WithTranslation & WithRouterProps

interface IState {
	email: string
	password: string
	shouldUseRefreshToken: boolean
	loading: boolean
	resend: boolean
	forgotPwd: boolean
	response: string
	pwdResponse: string
	showTerms: boolean
	activeImgIdx: number
	isSSOLoading: boolean
}

const CHROME_DOWNLOAD_URL = 'https://www.google.com/chrome/'

@track({})
// TASK migrate to React.FunctionComponent OR remove this if not possible
class LoginWithSSO extends React.Component<IProps, IState> {
	public state = {
		email:
			(this.props.router.location.search ? parseQueryString(this.props.router.location.search).email : '') ||
			sessionStorage.getItem('last_login_email') ||
			'',
		password: '',
		shouldUseRefreshToken: false,
		loading: false,
		resend: false,
		forgotPwd: this.props.router.location.search
			? isBoolPredicate(parseQueryString(this.props.router.location.search).forgotPwd)
			: false,
		response: '',
		pwdResponse: '',
		showTerms: false,
		activeImgIdx: 0,
		isSSOLoading: false,
	}

	private get returnTo() {
		const { returnTo } = parseQueryString<'returnTo'>(this.props.router.location.search)
		if (returnTo?.startsWith(Routes.Login)) {
			return Routes.Home
		}
		return returnTo ?? Routes.Home
	}

	private get imagesCarousel() {
		const { t } = this.props
		return [
			{
				text: t('login.travel-needs'),
				img: LoginGreenImage,
				backgroundColor: brightTurquoise,
			},
			{
				text: t('login.commute-with-peace'),
				img: LoginBlueImage,
				backgroundColor: darkBlue,
			},
			{
				text: t('login.consumer-grade'),
				img: LoginYellowImage,
				backgroundColor: yellow,
			},
		]
	}

	public render() {
		const { className, termsAccepted, user, t } = this.props
		const { loading, email, password, forgotPwd, response, showTerms, pwdResponse, resend, isSSOLoading } = this.state
		const shouldShowTerms = showTerms || (user && termsAccepted === false)
		const year = new Date().getFullYear()
		const { search, hash } = this.props.router.location

		const { isPwdLogin = false, returnTo } = parseQueryString(search) ?? {}
		const isLoginFail = (hash || returnTo)?.includes('error') ?? false

		return (
			<LoginPage
				className={className}
				contentProps={shouldShowTerms ? {} : { as: LoginPageContent }}
				offsetTop={0}
				width="100%"
			>
				{!shouldShowTerms ? (
					<LoginContainer>
						<LoginBox data-test="LoginForm">
							<Header>
								<h2>{t('login.welcome')}</h2>
								<LogoImage src={LogoTagline} alt="GetGoing" />
							</Header>
							{!isIE ? (
								<LoginForm onSubmit={(e) => this.onSubmit(e)}>
									{response ? <ResponseText variant="alert">{response}</ResponseText> : null}

									<FormTitle>
										{forgotPwd && !pwdResponse
											? t('login.dont-worry')
											: pwdResponse
												? t('login.check-email')
												: t('login.login-to-account')}
									</FormTitle>

									{forgotPwd && !pwdResponse ? (
										<RecoveryText>{t('login.enter-email-for-reset-link')}</RecoveryText>
									) : null}

									{!pwdResponse ? (
										<TextField
											id="login.email"
											label={t('login.email')}
											name="username"
											autoComplete="username"
											e2e={'LoginPage.Username'}
											value={email}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.setState({ email: e.target.value })}
										/>
									) : null}
									{!forgotPwd && (isPwdLogin || isLoginFail) ? (
										<TextField
											id="login.password"
											label={t('login.password')}
											name="password"
											autoComplete="current-password"
											value={password}
											type="password"
											e2e={'LoginPage.Password'}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.setState({ password: e.target.value })}
										/>
									) : null}

									{!pwdResponse && !forgotPwd && (
										<Checkbox
											label={t('login.remember-me')}
											checked={this.state.shouldUseRefreshToken}
											e2e="LoginPage.RememberMe"
											onToggle={(value) => this.setState({ shouldUseRefreshToken: value })}
										/>
									)}

									{pwdResponse ? (
										<div>
											<ResponseText>{pwdResponse}</ResponseText>
											<InfoBlock>
												<Trans i18nKey="login.click-to-resend">
													<a
														href={document.location.href}
														onClick={eventStopper.preventDefault(() => this.sendPwdReset(true))}
													>
														Click here to resend email
													</a>
													in the case you did not receive one within 5 minutes.
												</Trans>
											</InfoBlock>
											{resend ? <ResponseText>{t('login.done')}</ResponseText> : null}
										</div>
									) : null}

									{!pwdResponse ? (
										<Stack spacing={3}>
											{isPwdLogin || isLoginFail ? (
												<Button
													color="bluePrimary"
													disabled={loading}
													data-test={forgotPwd ? 'LoginPage.ResetPassword' : 'LoginPage.Login'}
												>
													{loading ? (
														<ButtonLoader color="contrast" />
													) : forgotPwd ? (
														t('login.reset-password')
													) : (
														t('login.login')
													)}
												</Button>
											) : null}
											{!forgotPwd && !(isPwdLogin || isLoginFail) && (
												<Button
													type="button"
													color="bluePrimary"
													disabled={loading || isSSOLoading}
													data-test={'LoginPage.LoginWithSSO'}
													onClick={() => this.loginWithSSO()}
												>
													{isSSOLoading || loading ? <ButtonLoader color="primary" /> : t('login.login', 'Login')}
												</Button>
											)}
										</Stack>
									) : null}

									{!forgotPwd ? (
										<div>
											<Trans i18nKey="login.forgot-password">
												Forgot your password? No worries... click
												<a
													style={{ textDecoration: 'none' }}
													href={document.location.href}
													onClick={eventStopper.preventDefault(
														() => (this.setState({ response: '' }), this.resetPwdForm()),
													)}
												>
													here
												</a>
											</Trans>
										</div>
									) : (
										<BackToLogin
											href={document.location.href}
											onClick={eventStopper.preventDefault(() => this.loginForm())}
										>
											{t('login.back-to-login')}
										</BackToLogin>
									)}
								</LoginForm>
							) : (
								<IeSection>
									<IeError>{t('login.ie-error.title')}</IeError>
									<div>{t('login.ie-error.subtitle')}</div>
									<ChromeLink onClick={() => openExternalUrl(CHROME_DOWNLOAD_URL, '_blank')}>
										{t('login.ie-error.chrome-link')}
									</ChromeLink>
								</IeSection>
							)}
						</LoginBox>

						<LoginImagesBox>
							<StyledCarousel
								showArrows={false}
								useKeyboardArrows={true}
								showStatus={false}
								showThumbs={false}
								dynamicHeight={true}
								infiniteLoop={true}
								autoPlay={true}
								stopOnHover={true}
								selectedItem={this.state.activeImgIdx}
								onChange={(idx: number) => this.setState({ activeImgIdx: idx })}
								onClickItem={(idx: number) => this.setState({ activeImgIdx: idx })}
							>
								{this.imagesCarousel.map((item, i) => (
									// eslint-disable-next-line react/no-array-index-key
									<LoginImage key={i} backgroundColor={item.backgroundColor}>
										<h2>{item.text}</h2>
										<Image dangerouslySetInnerHTML={{ __html: item.img }} />
									</LoginImage>
								))}
							</StyledCarousel>
						</LoginImagesBox>

						<Copyright>{t('login.copyright', { year })}</Copyright>
					</LoginContainer>
				) : (
					<TermsOfService {...this.props} />
				)}
			</LoginPage>
		)
	}

	private async onSubmit(e: React.FormEvent<HTMLFormElement>) {
		try {
			e.preventDefault()

			if (this.state.forgotPwd) {
				this.sendPwdReset()
				return
			}

			this.login()
		} catch (e) {
			this.setState({ loading: false })
		}
	}

	@track((_p, _s, _a, [user]: AsyncTrackResult<InstanceType<typeof LoginWithSSO>['login']>) => ({
		action: user ? 'LogIn' : 'LogInError',
	}))
	private async login() {
		const { t } = this.props
		const { email, password, shouldUseRefreshToken } = this.state
		const localeBeforeLogin: Locales | null = getCurrentLocale()

		return new Promise<User | undefined>((res) => {
			this.setState({ loading: true }, async () => {
				try {
					const details = await providerLogin({ store: getStore(), username: email, password, shouldUseRefreshToken })

					if (details.login_status === LoginStatus.UserExists) {
						this.props.router.navigate(this.returnTo, { replace: true })
					} else if (details.login_status === LoginStatus.NotAcceptedTos) {
						this.setState({ showTerms: true })
					}

					const currentLocale = details.user.language_code
					if (currentLocale !== null && currentLocale !== localeBeforeLogin) {
						this.handleLanguageChange(currentLocale)
					}

					this.props.showModalWindow()

					return res(details.user)
				} catch (e) {
					const err = (e.description ?? e.message) ? t('login.login-error') : t('login.server-error')
					this.setState({ loading: false, response: err })
					return res(undefined)
				}
			})
		}).then((user) => {
			const currentLocale = getCurrentLocale()
			if (localeBeforeLogin !== currentLocale) {
				this.handleLanguageChange(currentLocale)
			}
			return user
		})
	}

	@track((_p, _s, _a, [user]: AsyncTrackResult<InstanceType<typeof LoginWithSSO>['loginWithSSO']>) => ({
		action: user ? 'LogInWithSSO' : 'LogInWithSSOError',
	}))
	private loginWithSSO() {
		const { email } = this.state
		const domain = email.split('@')?.[1]
		if (email) {
			sessionStorage.setItem('last_login_email', email)
		}
		this.setState({ isSSOLoading: true })
		providerLoginSSO(getStore(), {
			loginHint: email || undefined,
			returnTo: this.returnTo,
			connection: `saml-${generateAutoAppName(domain).toLocaleLowerCase()}`,
		})
	}

	private handleLanguageChange(locale: Locales) {
		if (isUsingStorageFallback()) {
			changeLanguage(locale).catch(captureAndLogError)
		} else {
			window.location.reload()
		}
	}

	private async sendPwdReset(resend = false) {
		this.setState({ pwdResponse: '', loading: true, resend: false }, async () => {
			try {
				const resp = providerPwdReset(getStore(), this.state.email)
				this.setState({ pwdResponse: await resp, loading: false, resend })
			} catch (e) {
				this.setState({ pwdResponse: this.props.t('Something went wrong'), loading: false })
			}
		})
	}

	private resetPwdForm() {
		this.setState({ forgotPwd: true })
	}

	private loginForm() {
		this.setState({ forgotPwd: false, pwdResponse: '', resend: false, loading: false })
	}
}

const mapStateToProps = ({ auth }: ApplicationState): StateProps => {
	return {
		termsAccepted: auth.get('termsAccepted'),
		user: auth.get('user'),
	}
}

const mapDispatchToProps = (dispatch: Dispatch<IAction>): DispatchProps => {
	return {
		showModalWindow: () => {
			dispatch(CompanyTypeModalActions.showMyCompanyModal())
		},
	}
}

export default withTranslation()(
	connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
		mapStateToProps,
		mapDispatchToProps,
	)(withRouter(LoginWithSSO)),
)
